import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Button,
    ButtonGroup,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputLabel,
    FormControl
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import api from '../utils/api';

interface StatsData {
    guides_count: number;
    records_count: number;
    total_people: number;
}

interface TopGuide {
    name: string;
    short_name: string;
    tours_count: number;
    total_people: number;
}

interface TopTour {
    name: string;
    count: number;
}

interface HourlyData {
    hour: number;
    count: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57'];

const StatisticsPage: React.FC = () => {
    const [stats, setStats] = useState<StatsData | null>(null);
    const [topGuides, setTopGuides] = useState<TopGuide[]>([]);
    const [topTours, setTopTours] = useState<TopTour[]>([]);
    const [hourlyData, setHourlyData] = useState<HourlyData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [period, setPeriod] = useState<{ month_name: string; year: number } | null>(null);

    // Новые состояния для сравнения
    const [allPeriods, setAllPeriods] = useState<any[]>([]);
    const [selectedPeriodIds, setSelectedPeriodIds] = useState<number[]>([]);
    const [compareData, setCompareData] = useState<{ periods: any[], chartData: any[] }>({ periods: [], chartData: [] });

    const loadStatistics = async () => {
        try {
            setLoading(true);
            setError(null);

            // Загружаем список всех периодов для селекта
            const periodsRes = await api.get('/periods');
            const periodsList = Array.isArray(periodsRes.data) ? periodsRes.data : [];
            setAllPeriods(periodsList);
            
            // Если еще ничего не выбрано, выбираем активный период
            const active = periodsList.find((p: any) => p.is_active === 1);
            if (active && selectedPeriodIds.length === 0) {
                setSelectedPeriodIds([active.id]);
            }

            // Загружаем основную статистику
            const fullRes = await api.get('/stats/full');
            const data = fullRes.data;

            setStats(data.stats || {});
            setPeriod(data.period || null);
            setTopGuides(data.topGuides || []);
            setTopTours(data.topTours || []);

            // Загружаем часовое распределение
            try {
                const hourlyRes = await api.get('/stats/hourly-distribution');
                const hourly = hourlyRes.data || [];
                
                // Создаем полный диапазон часов с 8:00 до 23:00
                const fullRange: HourlyData[] = [];
                for (let hour = 8; hour <= 23; hour++) {
                    const found = hourly.find((h: any) => h.hour === hour);
                    fullRange.push({
                        hour: hour,
                        count: found ? found.count : 0
                    });
                }
                setHourlyData(fullRange);
            } catch (e) {
                console.error('Ошибка загрузки часового распределения:', e);
                // Если API нет, показываем пустой график
                setHourlyData([]);
            }

        } catch (err: any) {
            console.error('Ошибка загрузки статистики:', err);
            setError(err.response?.data?.error || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatistics();
    }, []);

    // Загрузка данных для сравнения при изменении выбора
    useEffect(() => {
        if (selectedPeriodIds.length === 0) {
            setCompareData({ periods: [], chartData: [] });
            return;
        }
        const fetchCompare = async () => {
            try {
                const res = await api.get(`/stats/compare?period_ids=${selectedPeriodIds.join(',')}`);
                setCompareData(res.data);
            } catch (e) {
                console.error('Ошибка загрузки сравнения:', e);
            }
        };
        fetchCompare();
    }, [selectedPeriodIds]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                
                <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Периоды для сравнения</InputLabel>
                    <Select
                        multiple
                        value={selectedPeriodIds}
                        onChange={(e) => setSelectedPeriodIds(e.target.value as number[])}
                        label="Периоды для сравнения"
                        renderValue={(selected) => 
                            selected.map(id => {
                                const p = allPeriods.find(x => x.id === id);
                                return p ? `${p.month_name} ${p.year}` : '';
                            }).join(', ')
                        }
                    >
                        {allPeriods.map((p: any) => (
                            <MenuItem key={p.id} value={p.id}>
                                <Checkbox checked={selectedPeriodIds.indexOf(p.id) > -1} />
                                <ListItemText primary={`${p.month_name} ${p.year}`} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                {period && (
                    <Chip
                        label={`Активный: ${period.month_name} ${period.year}`}
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 2 }}
                    />
                )}
            </Box>

            {/* Статистические карточки */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Экскурсоводов
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                {stats?.guides_count || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Записей в календаре
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                {stats?.records_count || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Всего человек
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                {stats?.total_people || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* График по часам */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Экскурсии по часам
                        </Typography>
                        {compareData.chartData.length > 0 && compareData.periods.length > 0 ? (
                            <Box sx={{ height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={compareData.chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="hour" 
                                            tickFormatter={(hour) => `${hour}:00`}
                                            interval={0}
                                        />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip 
                                            formatter={(value: any, name: any, props: any) => {
                                                const periodName = compareData.periods.find(p => p.key === name)?.name || name;
                                                return [`${value} экскурсий`, periodName];
                                            }}
                                            labelFormatter={(label) => `Время: ${label}:00`}
                                        />
                                        <Legend 
                                            formatter={(value) => {
                                                return compareData.periods.find(p => p.key === value)?.name || value;
                                            }}
                                        />
                                        {compareData.periods.map((p, idx) => (
                                            <Bar 
                                                key={p.key} 
                                                dataKey={p.key} 
                                                fill={COLORS[idx % COLORS.length]} 
                                                name={p.key}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                {selectedPeriodIds.length > 0 ? "Нет данных для отображения" : "Выберите периоды для сравнения"}
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Топ экскурсий */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Топ экскурсий
                        </Typography>
                        {topTours.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Название</TableCell>
                                            <TableCell align="right">Количество</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topTours.map((tour, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{tour.name}</TableCell>
                                                <TableCell align="right">{tour.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                Нет данных
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Топ гидов */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Топ экскурсоводов
                        </Typography>
                        {topGuides.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Экскурсовод</TableCell>
                                            <TableCell align="right">Экскурсий</TableCell>
                                            <TableCell align="right">Человек</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topGuides.map((guide, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{guide.short_name}</TableCell>
                                                <TableCell align="right">{guide.tours_count}</TableCell>
                                                <TableCell align="right">{guide.total_people}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                Нет данных
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default StatisticsPage;
